import { Stack, Text } from '@chakra-ui/react';
import type { CollabDeliveryMode } from '@fluenceur/types';
import { ChatBubble, MailOut, SendDiagonal } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import type { ProfileUnlockTarget } from '@/hooks/actions/use-unlock-creator-profile-action';
import {
  ChannelPicker,
  ConnectMailboxAffordance,
  type ChannelPickerRow,
} from '../outreach/channel-picker';
import { CollabMaskedEmailOffer } from './collab-masked-email-offer';

const K = 'invitations.modal.collab.wizard.send.channelPicker';

/** What the wizard's recipients + integrations allow, derived by the send step. */
export interface CollabChannelCapabilities {
  /** At least one reachable recipient carries an email: the option is shown. */
  emailAvailable: boolean;
  /** Email is usable: available AND a healthy mailbox is connected. */
  emailEnabled: boolean;
  /** Mono recipient with an open Instagram conversation. */
  dmAvailable: boolean;
  /**
   * The mailbox status is still resolving: the email row stays disabled but
   * shows its neutral description, without the "not connected" copy or the
   * connect affordance (no wrong-state flash).
   */
  emailStatusPending?: boolean;
  /** A known address waits for a connected mailbox before the email can leave. */
  emailNeedsMailbox?: boolean;
  /** The platform emails the recipient itself (an active affiliate, an applicant). */
  emailByPlatform?: boolean;
  /** The brand's plan masks the address of at least one recipient. */
  emailLockedByPlan?: boolean;
}

/**
 * Preselection order: email when usable, then DM, then the universal link. An
 * email the brand cannot send yet preselects nothing: the link is only ever an
 * explicit choice there, never a silent fall back.
 */
export const bestAvailableChannel = (c: CollabChannelCapabilities): CollabDeliveryMode | null =>
  c.emailEnabled ? 'email' : c.emailAvailable ? null : c.dmAvailable ? 'dm' : 'link';

/** Whether a previously chosen mode is still selectable under the capabilities. */
export const isChannelSelectable = (
  mode: CollabDeliveryMode,
  c: CollabChannelCapabilities
): boolean => (mode === 'email' ? c.emailEnabled : mode === 'dm' ? c.dmAvailable : true);

interface CollabChannelPickerProps {
  value: CollabDeliveryMode | null;
  onChange: (mode: CollabDeliveryMode) => void;
  capabilities: CollabChannelCapabilities;
  /** The masked creator a profile unlock would open (mono only). */
  unlockTarget?: ProfileUnlockTarget | null;
}

/**
 * The "Remise de la proposition" radio rows of the wizard's channel step: the
 * personal link (always), email through the connected mailbox (shown when a
 * recipient has an email; disabled with an inline connect affordance and a
 * request to connect when no mailbox is connected), and the Instagram DM (mono
 * with an open conversation). An address the plan masks offers a profile
 * unlock while the month has some left, else the plan offer.
 * A thin config wrapper over the shared {@link ChannelPicker}.
 */
export const CollabChannelPicker = ({
  value,
  onChange,
  capabilities,
  unlockTarget = null,
}: CollabChannelPickerProps) => {
  const { t } = useTranslation();
  const needsMailbox = !!capabilities.emailNeedsMailbox;
  const emailDescription = needsMailbox
    ? t(`${K}.email.notConnected`)
    : capabilities.emailEnabled && capabilities.emailByPlatform
      ? t(`${K}.email.descriptionPlatform`)
      : t(`${K}.email.description`);

  const rows: ChannelPickerRow<CollabDeliveryMode>[] = [
    {
      value: 'link',
      icon: ChatBubble,
      title: t(`${K}.link.title`),
      description: t(`${K}.link.description`),
    },
  ];
  if (capabilities.emailAvailable) {
    rows.push({
      value: 'email',
      icon: MailOut,
      disabled: !capabilities.emailEnabled,
      title: t(`${K}.email.title`),
      description: emailDescription,
      trailing: needsMailbox ? (
        <ConnectMailboxAffordance label={t(`${K}.email.connectLink`)} />
      ) : undefined,
    });
  }
  if (capabilities.dmAvailable) {
    rows.push({
      value: 'dm',
      icon: SendDiagonal,
      title: t(`${K}.dm.title`),
      description: t(`${K}.dm.description`),
    });
  }

  return (
    <Stack gap="2">
      <ChannelPicker rows={rows} value={value} onChange={onChange} />
      {needsMailbox && (
        <Text fontSize="11.5px" color="warning.text" lineHeight="1.5">
          {t(`${K}.email.connectRequired`)}
        </Text>
      )}
      {capabilities.emailLockedByPlan && <CollabMaskedEmailOffer unlockTarget={unlockTarget} />}
    </Stack>
  );
};
